import React, { useState, useMemo, useCallback } from "react";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import * as Router from "react-router-dom";
import { User } from "model/user";
import { urls } from "routes/urls";
import * as env from "shared/env";
import { MenuItem } from "./menu-item";
import { styles } from "./style";
import * as Icons from "react-feather";
import { FeatherIcon } from "component/shared/feather-icon"
import { RouterLinkWrapper } from "component/shared/router-link-wrapper";
import { getIntegrationByTypeId } from "redux/selector";
import { IntegrationType } from "type/integration-type";
import { useSelector } from "react-redux";
import { MobileAppLoginDialog } from "component/shared/mobile-app-login-dialog";
import classNames from "classnames";

interface Props extends Router.RouteComponentProps,
	WithStyles<typeof styles> {
	user: User;
}

const Component: React.FC<Props> = ({ user, classes = {}, location }) => {
	const [showMobileAppLoginDialog, setShowMobileAppLoginDialog] = useState(false);
	const wixIntegration = useSelector((state: any) => getIntegrationByTypeId(state, IntegrationType.WIX_APP));

	const isActive = useCallback((itemPath?: string, itemLabel?: string) => {
		if (!itemPath) return false;

		const pathname = location.pathname;

		// CRM/Leads section - matches lead-related pages
		if (itemLabel === "CRM" || itemLabel === "Leads") {
			return pathname.startsWith("/lead") || pathname === "/tasks";
		}

		// Markets section
		if (itemLabel === "Markets") {
			return pathname.startsWith("/market");
		}

		// Listings section
		if (itemLabel === "Listings") {
			return pathname.startsWith("/listing");
		}

		// Setup/Settings section
		if (itemLabel === "Setup" || itemLabel === "IDX Setup") {
			return pathname.startsWith("/settings");
		}

		// Communications/Campaigns section
		if (itemLabel === "Communications") {
			return pathname.startsWith("/campaign") || pathname.startsWith("/one-blast");
		}

		// Reports section
		if (itemLabel === "Reports") {
			return pathname.startsWith("/reports");
		}

		// CRM Tools section (oneCrm)
		if (itemLabel === "CRM Tools") {
			return pathname.startsWith("/one-team") || pathname.startsWith("/one-agent");
		}

		// Default: exact match or starts with
		return pathname.startsWith(itemPath);
	}, [location.pathname]);

	const renderLabel = useCallback((item: MenuItem) => (
		<>
			<FeatherIcon fontSize="inherit">
				<item.icon />
			</FeatherIcon>
			<Mui.Hidden mdDown>
				<div className={classes.label}>
					{item.label}
				</div>
			</Mui.Hidden>
		</>
	), [classes.label]);

	const items = useMemo(() => {
		let menuItems: MenuItem[] = [];
		if (!user.permissions.oneCrm) {
			menuItems = [
				((user.permissions.leads && user.ownsLeads) || user.admin) && {
					label: user.permissions.crm ? "CRM" : "Leads",
					className: "ihf-leads",
					icon: Icons.User,
					path: urls.landing(user),
				},
				user.admin && user.permissions.markets && {
					label: "Markets",
					className: "ihf-markets",
					icon: Icons.MapPin,
					path: urls.markets,
				},
				((user.permissions.modifyListings && user.ownsLeads) || user.admin) && {
					label: "Listings",
					className: "ihf-listings",
					icon: Icons.Home,
					path: urls.listingsActive,
				},
				user.admin && user.permissions.reporting && {
					label: "Reports",
					className: "ihf-reports",
					icon: Icons.BarChart2,
					path: urls.reportsOverview,
				},
				{
					label: "Setup",
					className: "ihf-setup",
					icon: Icons.Settings,
					// Profile now lives in the profile menu and hides the sidebar, so landing an agent
					// there leaves Setup with no Setup nav. Account is a real Setup page and renders
					// the sidebar -- but it is admin-only, and every other Setup child is too, so a
					// non-admin agent still has nowhere else to go (MAX-4139).
					path: user.type.agent
						? ((env.refreshProfile && user.admin) ? urls.settingsAccount : urls.settingsProfile)
						: urls.settingsOverview,
				},
				user.admin && user.permissions.campaigns && {
					label: "Communications",
					className: "ihf-communications",
					icon: Icons.Send,
					path: user.permissions.blastCampaigns ? urls.campaignNewEmail : urls.campaigns,
				},
				user.admin && user.website && !wixIntegration && {
					label: "Website",
					className: "ihf-website",
					icon: Icons.Sidebar,
					path: (!/^https?:\/\//i.test(user.website) ? "http://" + user.website : user.website),
					target: "_blank",
				},
			] as MenuItem[];
		} else {
			if (user.admin) {
				menuItems = [
					user.permissions.leads && {
						label: "Leads",
						className: "ihf-leads",
						icon: Icons.User,
						path: urls.landing(user),
					},
					user.permissions.markets && {
						label: "Markets",
						className: "ihf-markets",
						icon: Icons.MapPin,
						path: urls.markets,
					},
					{
						label: "Listings",
						className: "ihf-listings",
						icon: Icons.Home,
						path: urls.listingsActive,
					},
					{
						label: "IDX Setup",
						className: "ihf-setup",
						icon: Icons.Settings,
						path: urls.settingsOverview,
					},
					user.onePermissions.sendEmail && {
						label: "Communications",
						icon: Icons.Send,
						path: urls.oneBlastEmail,
					},
					{
						label: "CRM Tools",
						icon: Icons.Sliders,
						path: urls.oneTeamList,
					},
					user.website && !wixIntegration && {
						label: "Website",
						className: "ihf-website",
						icon: Icons.Sidebar,
						path: (!/^https?:\/\//i.test(user.website) ? "http://" + user.website : user.website),
						target: "_blank",
					},
					{
						label: "Calendar",
						icon: Icons.Calendar,
						path: urls.oneCalendar,
						target: "_blank",
					},
				] as MenuItem[];
			} else if (user.type.agent) {
				menuItems = [
					{
						label: "Listings",
						className: "ihf-listings",
						icon: Icons.Home,
						path: urls.listingsActive,
					},
					{
						label: "IDX Setup",
						className: "ihf-setup",
						icon: Icons.Settings,
						path: urls.settingsOverview,
					},
					user.onePermissions.sendEmail && {
						label: "Communications",
						icon: Icons.Send,
						path: urls.oneBlastEmail,
					},
					{
						label: "CRM Tools",
						icon: Icons.Sliders,
						path: urls.oneTeamList,
					},
					user.website && {
						label: "Website",
						className: "ihf-website",
						icon: Icons.Sidebar,
						path: (!/^https?:\/\//i.test(user.website) ? "http://" + user.website : user.website),
						target: "_blank",
					},
					{
						label: "Calendar",
						icon: Icons.Calendar,
						path: urls.oneCalendar,
						target: "_blank",
					},

				] as MenuItem[];
			}
		}

		const mobileLogin: MenuItem = {
			label: "Mobile App Login",
			icon: Icons.Smartphone,
			clickable: true,
			onClick: () => {
				setShowMobileAppLoginDialog(true);
			}
		} as MenuItem;
		if (!user.boardIds.includes(251)) {
			menuItems.push(mobileLogin);
		}
		menuItems = menuItems.filter(item => !!item);
		return menuItems;
	}, [user, wixIntegration]);

	const renderList = useCallback(() => {
		return items.map((item, index) => {
			const active = isActive(item.path, item.label);
			const buttonClasses = classNames(
				item.className,
				active && classes.topMenuItemActive
			);

			if (item.target) {
				return (
					<Mui.Button
						key={index}
						className={buttonClasses}
						classes={{
							root: classes.topMenuItem,
						}}
						href={item.path}
						target={item.target}
					>
						{renderLabel(item)}
					</Mui.Button>
				);
			} else if (item.clickable) {
				return (
					<Mui.Button
						key={index}
						className={buttonClasses}
						classes={{
							root: classes.topMenuItem,
						}}
						onClick={item.onClick}
					>
						{renderLabel(item)}
					</Mui.Button>
				);
			} else {
				return (
					<Mui.Button
						key={index}
						className={buttonClasses}
						classes={{
							root: classes.topMenuItem,
						}}
						component={RouterLinkWrapper}
						to={item.path}
					>
						{renderLabel(item)}
					</Mui.Button>
				);
			}
		});
	}, [items, classes.topMenuItem, classes.topMenuItemActive, renderLabel, isActive]);

	return (
		<React.Fragment>
			<MobileAppLoginDialog
				user={user}
				open={showMobileAppLoginDialog}
				onClick={() => setShowMobileAppLoginDialog(false)}
			/>
			<div className={classes.navItems}>
				{renderList()}
			</div>
		</React.Fragment>
	);
};

export const TopMenu = withStyles(Router.withRouter(Component), styles);
